import {
  MY_ACCOUNT,
  NAVIGATION,
  WISHLIST_SAVE,
  NEWEST_TO_OLDEST,
  OLDEST_TO_NEWEST,
  LISTS,
  MY_LISTS,
  KOHLER,
  SHARE_LIST,
  ACCOUNT_SETTINGS,
  ADD_ALL_T0_CART,
  ADD_T0_CART,
  LIST_DETAIL,
  CLICK,
  LIST_DETAILS,
  SELECTED_LISTS,
} from "@/components/MyFavorites/v1/MyList/constant"
import { MY_LISTS_PAGE_PATH, PRODUCT_BEST_SELLERS } from "@/constants/routes"

const sanitizeForDataLayer = obj => {
  if (Array.isArray(obj)) {
    return obj.map(sanitizeForDataLayer)
  } else if (obj && typeof obj === "object") {
    const sanitized = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const val = obj[key]
        if (
          val === undefined ||
          val === null ||
          (typeof val === "string" && val.trim() === "")
        ) {
          sanitized[key] = "n/a"
        } else if (typeof val === "object") {
          sanitized[key] = sanitizeForDataLayer(val)
        } else {
          sanitized[key] = val
        }
      }
    }
    return sanitized
  }
  return obj === undefined || obj === null || obj === "" ? "n/a" : obj
}

const pushToAdobeDataLayer = (eventInfo, listInfo) => {
  const { adobeDataLayer: { getState } = {} } = window
  const pageState = getState?.("page") || {}

  const page = pageState
  if (!page.category) page.category = {}
  page.category.pageType = "my list"
  window.adobeDataLayer.push({
    event: "cmp:click",
    eventInfo,
    ...(listInfo ? { listInfo } : {}),
    page,
  })
}

const pushToAdobeDataLayerDetails = (eventInfo, listInfo, listName = "") => {
  const { adobeDataLayer: { getState } = {} } = window
  const pageState = getState?.("page") || {}

  const page = pageState
  if (!page.category) page.category = {}
  page.category.pageType = `${KOHLER}:${MY_ACCOUNT}:${MY_LISTS}:detail`
  page.pageInfo.pageName = `${KOHLER}:${MY_ACCOUNT}:${MY_LISTS}:detail:${listName}`
  window.adobeDataLayer.push({
    event: "cmp:click",
    eventInfo,
    ...(listInfo ? { listInfo } : {}),
    page,
  })
}

export const dataLayerListCheckboxEvent = ({
  isSelectAll,
  isChecked,
  listName,
  checkedItems,
  productData,
  productInfo,
  quantityObject = {},
  listItemsPrice = {},
  persona,
}) => {
  // Helper to map product to analytics attributes (same as add to cart)
  const mapProductToAnalytics = (product, persona) => {
    if (!product) return {}
    let color = ""
    let image = ""
    if (product?.[`Color.${persona}.Details_ss`]) {
      const productInfoRec = product?.[`Color.${persona}.Details_ss`]?.filter(
        rec => {
          return product["masterSKU_s"] === rec.split("|")[0]
        }
      )
      if (productInfoRec.length) {
        const values = productInfoRec[0].split("|")
        color = values[1]
        image = values[5]
      }
    }
    return {
      category: (product["Product_Category"] ?? "").toLowerCase(),
      description: (
        product["ProductDescriptionProductShort_s"] ?? ""
      ).toLowerCase(),
      productID: (product["ctId_s"] ?? "").toLowerCase(),
      productName: (product["productName_s"] ?? "").toLowerCase(),
      quantity: (quantityObject &&
      product["masterSKU_s"] &&
      typeof quantityObject[product["masterSKU_s"]] !== "undefined"
        ? quantityObject[product["masterSKU_s"]]
        : product.quantity || "1"
      ).toString(),
      productSalePrice: `${
        listItemsPrice?.[`${product["masterSKU_s"]}-discount`] ||
        listItemsPrice[product["masterSKU_s"]] ||
        product[`priceList.${persona}.finalPrice_d`] ||
        ""
      }`,
      productSku: product["masterSKU_s"]
        ? `k-${product["masterSKU_s"]}`.toLowerCase()
        : "n/a",
      defaultImageName: (image ?? "").toLowerCase(),
      productColor:
        color && color.trim() !== "" && color !== "null"
          ? `${color}`.toLowerCase()
          : "n/a",
    }
  }

  let finalProductInfo
  if (isSelectAll) {
    // If unchecking select all (isChecked === false), send all products' info
    if (isChecked) {
      finalProductInfo =
        checkedItems && productData
          ? checkedItems
              ?.map(sku => productData.find(p => p.masterSKU_s === sku))
              ?.filter(Boolean)
              .map(product => mapProductToAnalytics(product, persona))
          : []
    } else {
      // Unchecking select all: send all products
      finalProductInfo = productData
        ? productData.map(product => mapProductToAnalytics(product, persona))
        : []
    }
  } else {
    // productInfo is always [product] or []
    finalProductInfo = Array.isArray(productInfo)
      ? productInfo.map(product => mapProductToAnalytics(product, persona))
      : productInfo
      ? [mapProductToAnalytics(productInfo, persona)]
      : []
    // If only one, flatten to object (like add to cart)
    if (finalProductInfo.length === 1) finalProductInfo = finalProductInfo[0]
  }

  const { adobeDataLayer: { getState } = {} } = window
  const page = getState?.("page") || {}
  const eventObj = {
    event: "cmp:click",
    eventInfo: {
      clickInternalLinks: "true",
      eventAction: `my account:my lists:${
        isSelectAll ? "select all" : "single"
      }`,
      eventMsg: isSelectAll ? "Bulk add to cart initiated" : "n/a",
      eventName: `my account:my lists:${isSelectAll ? "select all" : "single"}`,
      eventStatus: "n/a",
      eventType: "interaction",
      internalLinkName: `${isSelectAll ? "select all items" : "single item"} ${
        isChecked ? "checked" : "unchecked"
      } from my list`,
      internalLinkPosition: "my account:my lists",
      internalLinkType: `my account:my lists:${listName}`,
      internalLinkURL: "n/a",
      internalLinkZoneName: "my account:my lists",
    },
    page,
    productInfo: finalProductInfo,
  }
  window.adobeDataLayer.push(sanitizeForDataLayer(eventObj))
}

export const dataLayerViewShareListButton = (listName, listInfo) => {
  const { adobeDataLayer: { getState } = {} } = window
  const pageState = getState?.("page") || {}
  const page = pageState
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${MY_LISTS}:${LISTS}:${SHARE_LIST}`,
    eventMsg: "n/a",
    eventName: `${MY_ACCOUNT}:${MY_LISTS}:${LISTS}:${SHARE_LIST}`,
    eventStatus: "n/a",
    eventType: `${CLICK}`,
    internalLinkName: `${SHARE_LIST}`,
    internalLinkPosition: `${MY_ACCOUNT}:${MY_LISTS}:${LISTS}`,
    internalLinkType: `${MY_ACCOUNT}:${MY_LISTS}:${SHARE_LIST}:${listName}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${MY_ACCOUNT}:${MY_LISTS}:${SHARE_LIST}:${listName}`,
  }
  const category = {
    pageType: `${KOHLER}:${MY_ACCOUNT}:${MY_LISTS}`,
    primaryCategory: `${KOHLER}:${MY_ACCOUNT}`,
    subCategory1: `${MY_ACCOUNT}:${MY_LISTS}`,
    subCategory2: `${KOHLER}:${MY_ACCOUNT}:${MY_LISTS}`,
    subCategoryID: `${MY_LISTS}`,
    subCategoryID2: "n/a",
    subCategoryName: `${MY_LISTS}`,
  }
  if (!page.category) page.category = {}
  page.category = { ...page.category, ...category }
  window.adobeDataLayer.push({
    event: "cmp:click",
    eventInfo,
    ...(listInfo ? { listInfo } : {}),
    page,
  })
}

export const dataLayerViewListButton = (
  myListTitle,
  viewListCTATitle,
  listName,
  listInfo,
  idView
) => {
  const myListHeading = myListTitle?.toLowerCase()
  const viewListCTAHeading = viewListCTATitle?.toLowerCase()
  const url = MY_LISTS_PAGE_PATH
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${myListHeading}:${viewListCTAHeading}`,
    eventMsg: "n/a",
    eventName: `${MY_ACCOUNT}:${myListHeading}:${viewListCTAHeading}`,
    eventStatus: "n/a",
    eventType: `${NAVIGATION}`,
    internalLinkName: `${viewListCTAHeading}`,
    internalLinkPosition: `${MY_ACCOUNT}:${myListHeading}`,
    internalLinkType: `${MY_ACCOUNT}:${myListHeading}:${listName}`,
    internalLinkURL: `${window.location.origin}${url}${idView}`,
    internalLinkZoneName: `${MY_ACCOUNT}:${myListHeading}`,
  }
  pushToAdobeDataLayerDetails(eventInfo, listInfo, listName)
}

export const dataLayerShopBestSellerButton = (
  myListTitle,
  shopBestSellerCTATitle,
  listName,
  listInfo
) => {
  const myListHeading = myListTitle?.toLowerCase()
  const shopBestSellerCTAHeading = shopBestSellerCTATitle?.toLowerCase()
  const urlP = PRODUCT_BEST_SELLERS
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${myListHeading}:${shopBestSellerCTAHeading}`,
    eventMsg: "n/a",
    eventName: `${MY_ACCOUNT}:${myListHeading}:${shopBestSellerCTAHeading}`,
    eventStatus: "n/a",
    eventType: `${NAVIGATION}`,
    internalLinkName: `${shopBestSellerCTAHeading}`,
    internalLinkPosition: `${MY_ACCOUNT}:${myListHeading}`,
    internalLinkType: `${MY_ACCOUNT}:${myListHeading}:${listName}`,
    internalLinkURL: `${window.location.origin}${urlP}`,
    internalLinkZoneName: `${MY_ACCOUNT}:${myListHeading}`,
  }
  pushToAdobeDataLayer(eventInfo, listInfo)
}

export const dataLayerDeleteListButton = (
  myListTitle,
  deleteListTitle,
  listName,
  listInfo
) => {
  const myListHeading = myListTitle?.toLowerCase()
  const deleteListHeading = deleteListTitle?.toLowerCase()
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${myListHeading}:${deleteListHeading}`,
    eventMsg: "n/a",
    eventName: `${MY_ACCOUNT}:${myListHeading}:${deleteListHeading}`,
    eventStatus: "n/a",
    eventType: `${NAVIGATION}`,
    internalLinkName: `${deleteListHeading}`,
    internalLinkPosition: `${MY_ACCOUNT}:${myListHeading}`,
    internalLinkType: `${MY_ACCOUNT}:${myListHeading}:${listName}`,
    internalLinkURL: `n/a`,
    internalLinkZoneName: `${MY_ACCOUNT}:${myListHeading}`,
  }
  pushToAdobeDataLayer(eventInfo, listInfo)
}

export const dataLayerCreateNewMyListButton = (
  myListTitle,
  createNewListTitle
) => {
  const myListHeading = myListTitle?.toLowerCase()
  const createNewListHeading = createNewListTitle?.toLowerCase()
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${myListHeading}:${createNewListHeading}`,
    eventMsg: "n/a",
    eventName: `${MY_ACCOUNT}:${myListHeading}:${createNewListHeading}`,
    eventStatus: "n/a",
    eventType: `${WISHLIST_SAVE}`,
    internalLinkName: `${createNewListHeading}`,
    internalLinkPosition: `${MY_ACCOUNT}:${myListHeading}`,
    internalLinkType: `${MY_ACCOUNT}:${myListHeading}`,
    internalLinkURL: `n/a`,
    internalLinkZoneName: `${MY_ACCOUNT}:${myListHeading}`,
  }
  pushToAdobeDataLayer(eventInfo)
}

export const dataLayerlistTab = (myListTitle, tabListTitle) => {
  const myListHeading = myListTitle?.toLowerCase()
  const listTabHeading = tabListTitle?.toLowerCase()
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${myListHeading}:${listTabHeading}`,
    eventMsg: "n/a",
    eventName: `${MY_ACCOUNT}:${myListHeading}:${listTabHeading}`,
    eventStatus: "n/a",
    eventType: `${NAVIGATION}`,
    internalLinkName: `${listTabHeading}`,
    internalLinkPosition: `${MY_ACCOUNT}:${myListHeading}`,
    internalLinkType: `${MY_ACCOUNT}:${myListHeading}:${listTabHeading}`,
    internalLinkURL: `n/a`,
    internalLinkZoneName: `${MY_ACCOUNT}:${myListHeading}`,
  }
  pushToAdobeDataLayer(eventInfo)
}

export const dataLayerMediaTab = (myListTitle, mediaTabTitle) => {
  const myListHeading = myListTitle?.toLowerCase()
  const mediaTabHeading = mediaTabTitle?.toLowerCase()
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${myListHeading}:${mediaTabHeading}`,
    eventMsg: "n/a",
    eventName: `${MY_ACCOUNT}:${myListHeading}:${mediaTabHeading}`,
    eventStatus: "n/a",
    eventType: `${NAVIGATION}`,
    internalLinkName: `${mediaTabHeading}`,
    internalLinkPosition: `${MY_ACCOUNT}:${myListHeading}`,
    internalLinkType: `${MY_ACCOUNT}:${myListHeading}${mediaTabHeading}`,
    internalLinkURL: `n/a`,
    internalLinkZoneName: `${MY_ACCOUNT}:${myListHeading}`,
  }
  pushToAdobeDataLayer(eventInfo)
}

export const dataLayerNewestToOldest = (myListTitle, sortByTitle) => {
  const myListHeading = myListTitle?.toLowerCase()
  const sortByHeading = sortByTitle?.toLowerCase()
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${myListHeading}:${LISTS}:${sortByHeading}`,
    eventMsg: "n/a",
    eventName: `${MY_ACCOUNT}:${myListHeading}:${LISTS}:${sortByHeading}`,
    eventStatus: "n/a",
    eventType: `${NAVIGATION}`,
    internalLinkName: `${NEWEST_TO_OLDEST}`,
    internalLinkPosition: `${MY_ACCOUNT}:${myListHeading}:${LISTS}`,
    internalLinkType: `${MY_ACCOUNT}:${myListHeading}:${NEWEST_TO_OLDEST}`,
    internalLinkURL: `n/a`,
    internalLinkZoneName: `${MY_ACCOUNT}:${myListHeading}:${LISTS}`,
  }
  pushToAdobeDataLayer(eventInfo)
}

export const dataLayerOldestToNewest = (myListTitle, sortByTitle) => {
  const myListHeading = myListTitle?.toLowerCase()
  const sortByHeading = sortByTitle?.toLowerCase()
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${myListHeading}:${LISTS}:${sortByHeading}`,
    eventMsg: "n/a",
    eventName: `${MY_ACCOUNT}:${myListHeading}:${LISTS}:${sortByHeading}`,
    eventStatus: "n/a",
    eventType: `${NAVIGATION}`,
    internalLinkName: `${OLDEST_TO_NEWEST}`,
    internalLinkPosition: `${MY_ACCOUNT}:${myListHeading}:${LISTS}`,
    internalLinkType: `${MY_ACCOUNT}:${myListHeading}:${OLDEST_TO_NEWEST}`,
    internalLinkURL: `n/a`,
    internalLinkZoneName: `${MY_ACCOUNT}:${myListHeading}:${LISTS}`,
  }
  pushToAdobeDataLayer(eventInfo)
}

const pushListDetailsAdobeDataLayer = data => {
  const {
    name = "",
    eventInfo = {},
    productsCount = 0,
    productInfo = [],
    shareListPage = false,
    listDetailsShareList = false,
  } = data
  const { adobeDataLayer: { getState } = {} } = window
  const page = getState?.("page") || {}
  page.category.pageType = `${MY_LISTS}:${name}:${productsCount}`
  if (shareListPage) {
    page.category.pageType = `${KOHLER}:${SHARE_LIST}`
    page.category.subCategory1 = `${MY_ACCOUNT}:${MY_LISTS}`
    page.category.subCategory2 = `${KOHLER}:${MY_ACCOUNT}:${MY_LISTS}`
    page.pageInfo.pageName = `${KOHLER}:${SHARE_LIST}:${name}`
    page.pageInfo.title = `${MY_LISTS}|${KOHLER}`
  } else if (listDetailsShareList) {
    page.category.pageType = `${KOHLER}:${MY_ACCOUNT}:${MY_LISTS}:${LIST_DETAIL}`
    page.pageInfo.pageName = `${KOHLER}:${MY_ACCOUNT}:${MY_LISTS}:${LIST_DETAIL}:${name}`
  } else {
    page.category.pageType =
      `${MY_LISTS}:${name}:${LIST_DETAIL}:${productsCount}`?.toLowerCase()
  }
  window.adobeDataLayer.push({
    event: "cmp:click",
    eventInfo,
    productInfo: productInfo,
    page,
  })
}
export const dataLayerShareListPageEvents = data => {
  const {
    name = "",
    actionType = "",
    productsCount = 0,
    records = [],
    quantityObject = {},
    eventStatus = "",
    eventMsg = "",
    eventType = "",
    listTotalPrice = "",
    listItemsPrice = {},
    persona,
  } = data

  let calActionType = actionType
  if (records.length > 1 && actionType === ADD_T0_CART) {
    calActionType = ADD_ALL_T0_CART
  }
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${KOHLER}:${SHARE_LIST}:${calActionType}`,
    eventMsg: eventMsg || "n/a",
    eventName: `${KOHLER}:${SHARE_LIST}:${calActionType}`,
    eventStatus: eventStatus || "n/a",
    eventType: eventType ?? `${NAVIGATION}`,
    internalLinkName: `${SHARE_LIST}:${calActionType}`,
    internalLinkPosition: `${KOHLER}:${SHARE_LIST}:${LISTS}`,
    internalLinkType: `${KOHLER}:${SHARE_LIST}:${name}`,
    internalLinkURL: `n/a`,
    internalLinkZoneName: `${KOHLER}:${SHARE_LIST}:${name}:${listTotalPrice}`,
  }
  const productInfo = dataLayerEventDetailsPageProductInfo(
    records,
    quantityObject,
    listItemsPrice,
    persona
  )
  pushListDetailsAdobeDataLayer({
    eventInfo,
    productsCount,
    productInfo,
    name,
    shareListPage: true,
  })
}

export const dataLayerListDetailAddToCartPageEvents = data => {
  const {
    name = "",
    actionType = "",
    productsCount = 0,
    eventStatus = "",
    eventMsg = "",
    eventType = "",
    items = [],
    records = [],
    quantityObject = {},
    handleSingleProduct = false,
    ctaLabel = "",
    productInfo = [],
    listItemsPrice,
    persona,
  } = data
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${MY_LISTS}:${actionType}`,
    eventMsg: eventMsg || "n/a",
    eventName: `${MY_ACCOUNT}:${MY_LISTS}:${actionType}`,
    eventStatus: `${eventStatus || "n/a"}`.toLowerCase(),
    eventType: eventType,
    internalLinkName: `${ctaLabel || actionType}`.toLowerCase(),
    internalLinkPosition: `${MY_ACCOUNT}:${MY_LISTS}`,
    internalLinkType: `${MY_ACCOUNT}:${MY_LISTS}:${name}`,
    internalLinkURL: `n/a`,
    internalLinkZoneName: `${MY_ACCOUNT}:${MY_LISTS}`,
  }
  let productInfoNew = productInfo
  if (Array.isArray(productInfo) && productInfo.length === 1) {
    // Only one product: pass as object, not array with proper mapping
    const item = productInfo[0]
    // Find the matching record for this SKU
    const record =
      records.find(
        rec =>
          rec["masterSKU_s"] ===
          (item.productSku || item.productID || item.sku || item.masterSKU_s)
      ) || {}
    // Compute color and image
    let color = ""
    let image = ""
    if (record?.[`Color.${persona}.Details_ss`]) {
      const productInfoRec = record[`Color.${persona}.Details_ss`]?.filter(
        recStr => {
          return record["masterSKU_s"] === recStr.split("|")[0]
        }
      )
      if (productInfoRec.length) {
        const values = productInfoRec[0].split("|")
        color = values[1]
        image = values[5]
      }
    }
    productInfoNew = {
      category: `${record["Product_Category"] ?? ""}`.toLowerCase(),
      description: `${
        record["ProductDescriptionProductShort_s"] ?? ""
      }`.toLowerCase(),
      productID: `${record["ctId_s"] ?? ""}`.toLowerCase(),
      productName: `${record["productName_s"] ?? ""}`.toLowerCase(),
      quantity: `${quantityObject[record["masterSKU_s"]] ?? ""}`.toLowerCase(),
      productSalePrice: `${
        listItemsPrice?.[`${record["masterSKU_s"]}-discount`] ||
        listItemsPrice[record["masterSKU_s"]] ||
        ""
      }`,
      productSku: record["masterSKU_s"]
        ? `k-${record["masterSKU_s"]}`.toLowerCase()
        : "n/a",
      defaultImageName: `${image}`.toLowerCase(),
      productColor:
        color && color.trim() !== "" && color !== "null"
          ? `${color}`.toLowerCase()
          : "n/a",
    }
  } else if (Array.isArray(productInfo) && productInfo.length > 1) {
    // Multiple products: pass productInfo array as is
    productInfoNew = productInfo.map(item => {
      // Find the matching record for this SKU
      const record =
        records.find(
          rec =>
            rec["masterSKU_s"] ===
            (item.productSku || item.productID || item.sku || item.masterSKU_s)
        ) || {}
      // Compute color and image
      let color = ""
      let image = ""
      if (record[`Color.${persona}.Details_ss`]) {
        const productInfoRec = record?.[`Color.${persona}.Details_ss`]?.filter(
          recStr => {
            return record["masterSKU_s"] === recStr.split("|")[0]
          }
        )
        if (productInfoRec.length) {
          const values = productInfoRec[0].split("|")
          color = values[1]
          image = values[5]
        }
      }
      return {
        category: `${record["Product_Category"] ?? ""}`.toLowerCase(),
        description: `${
          record["ProductDescriptionProductShort_s"] ?? ""
        }`.toLowerCase(),
        productID: `${record["ctId_s"] ?? ""}`.toLowerCase(),
        productName: `${record["productName_s"] ?? ""}`.toLowerCase(),
        quantity: `${
          quantityObject[record["masterSKU_s"]] ?? ""
        }`.toLowerCase(),
        productSalePrice: `${
          listItemsPrice?.[`${record["masterSKU_s"]}-discount`] ||
          listItemsPrice[record["masterSKU_s"]] ||
          ""
        }`,
        productSku: record["masterSKU_s"]
          ? `k-${record["masterSKU_s"]}`.toLowerCase()
          : "n/a",
        defaultImageName: `${image}`.toLowerCase(),
        productColor:
          color && color.trim() !== "" && color !== "null"
            ? `${color}`.toLowerCase()
            : "n/a",
      }
    })
  }
  if (handleSingleProduct) {
    const colorAndImage = { color: "", image: "" }
    let itemsRecord = {}
    const record = records?.filter(
      rec => rec["masterSKU_s"] === items[0].productID.substring(2)
    )
    if (record.length) {
      const productInfoRec = record?.[0]?.[
        `Color.${persona}.Details_ss`
      ]?.filter(rec => {
        return items[0].productID.substring(2) === rec.split("|")[0]
      })
      if (productInfoRec.length) {
        const values = productInfoRec[0].split("|")
        colorAndImage.color = values[1]
        colorAndImage.image = values[5]
      }
      itemsRecord = record[0]
    }
    productInfoNew = {
      category: `${itemsRecord["Product_Category"] ?? ""}`.toLowerCase(),
      description: `${
        itemsRecord["ProductDescriptionProductShort_s"] ?? ""
      }`.toLowerCase(),
      productID: `${itemsRecord["ctId_s"] ?? ""}`.toLowerCase(),
      productName: `${itemsRecord["productName_s"] ?? ""}`.toLowerCase(),
      quantity: `${
        quantityObject[itemsRecord["masterSKU_s"]] ?? ""
      }`.toLowerCase(),
      productSalePrice: `${
        listItemsPrice?.[`${itemsRecord?.["masterSKU_s"]}-discount`] ||
        listItemsPrice[itemsRecord["masterSKU_s"]]
      }`,
      productSku: itemsRecord["masterSKU_s"]
        ? `k-${itemsRecord["masterSKU_s"]}`.toLowerCase()
        : "n/a",
      defaultImageName: `${colorAndImage?.image}`.toLowerCase(),
      productColor:
        colorAndImage?.color &&
        colorAndImage.color.trim() !== "" &&
        colorAndImage.color !== "null"
          ? `${colorAndImage.color}`.toLowerCase()
          : "n/a",
    }
  }
  pushListDetailsAddToCartAdobeDataLayer(
    sanitizeForDataLayer({
      eventInfo,
      productsCount,
      name,
      items,
      productInfo: productInfoNew,
    })
  )
}

const pushListDetailsAddToCartAdobeDataLayer = data => {
  const {
    name = "",
    eventInfo = {},
    productsCount = 0,
    items = [],
    productInfo = false,
  } = data
  const { adobeDataLayer: { getState } = {} } = window
  const page = getState?.("page") || {}
  page.category.pageType =
    `${MY_LISTS}:${name}:${LIST_DETAIL}:${productsCount}`?.toLowerCase()
  page.item = items
  window.adobeDataLayer.push(
    sanitizeForDataLayer({
      event: "cmp:click",
      eventInfo,
      page,
      productInfo,
    })
  )
}

const dataLayerEventDetailsPageProductInfo = (
  records,
  quantityObject,
  listItemsPrice = {},
  persona
) => {
  return records.map(product => {
    const colorAndImage = { color: "", image: "" }
    if (product[`Color.${persona}.Details_ss`]) {
      const productInfoRec = product?.[`Color.${persona}.Details_ss`]?.filter(
        rec => {
          return product["masterSKU_s"] === rec.split("|")[0]
        }
      )
      if (productInfoRec.length) {
        const values = productInfoRec[0].split("|")
        colorAndImage.color = values[1]
        colorAndImage.image = values[5]
      }
    }
    return {
      category: product["Product_Category"] ?? "",
      description: product["ProductDescriptionProductShort_s"] ?? "",
      productID: product["ctId_s"] ?? "",
      productName: product["productName_s"] ?? "",
      quantity: quantityObject[product["masterSKU_s"]] ?? "",
      productSalePrice: `${
        listItemsPrice?.[`${product["masterSKU_s"]}-discount`] ||
        listItemsPrice[product["masterSKU_s"]] ||
        product[`priceList.${persona}.finalPrice_d`] ||
        ""
      }`,
      productSku: product["masterSKU_s"] ? product["masterSKU_s"] : "n/a",
      defaultImageName: colorAndImage?.image,
      productColor: colorAndImage?.color,
    }
  })
}

export const dataLayerListDetailPageShareListEvents = data => {
  const {
    name = "",
    actionType = "",
    productsCount = 0,
    records = [],
    quantityObject = {},
    eventStatus = "",
    eventMsg = "",
    listItemsPrice = {},
    persona,
    listTotalPrice = "",
  } = data
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${MY_LISTS}:${LIST_DETAILS}:${actionType}`,
    eventMsg: eventMsg || "n/a",
    eventName: `${MY_ACCOUNT}:${MY_LISTS}:${LIST_DETAILS}:${actionType}`,
    eventStatus: eventStatus || "n/a",
    eventType: `${CLICK}`,
    internalLinkName: `${actionType}`,
    internalLinkPosition: `${MY_ACCOUNT}:${MY_LISTS}:${LIST_DETAILS}`,
    internalLinkType: `${MY_ACCOUNT}:${MY_LISTS}:${LIST_DETAILS}:${actionType}:${name}`,
    internalLinkURL: `n/a`,
    internalLinkZoneName: `${MY_ACCOUNT}:${MY_LISTS}:${SELECTED_LISTS}:${name}:${listTotalPrice}`,
  }
  const productInfo = dataLayerEventDetailsPageProductInfo(
    records,
    quantityObject,
    listItemsPrice,
    persona
  )
  pushListDetailsAdobeDataLayer({
    eventInfo,
    productsCount,
    productInfo,
    name,
    listDetailsShareList: true,
  })
}
export const dataLayerListDetailPageEvents = data => {
  const {
    name = "",
    actionType = "",
    productsCount = 0,
    records = [],
    quantityObject = {},
    productName = "",
    eventStatus = "",
    eventMsg = "",
    eventType = "",
    listItemsPrice = {},
    persona,
  } = data
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${MY_ACCOUNT}:${MY_LISTS}:${actionType}`,
    eventMsg: eventMsg || "n/a",
    eventName: `${MY_ACCOUNT}:${MY_LISTS}:${actionType}`,
    eventStatus: eventStatus || "n/a",
    eventType: eventType ?? `${NAVIGATION}`,
    internalLinkName:
      actionType === "product navigation" ? `${productName}` : `${actionType}`,
    internalLinkPosition: `${MY_ACCOUNT}:${MY_LISTS}`,
    internalLinkType: `${MY_ACCOUNT}:${MY_LISTS}:${name}`,
    internalLinkURL: `n/a`,
    internalLinkZoneName: `${MY_ACCOUNT}:${MY_LISTS}`,
  }
  const productInfo = dataLayerEventDetailsPageProductInfo(
    records,
    quantityObject,
    listItemsPrice,
    persona
  )
  pushListDetailsAdobeDataLayer({ eventInfo, productsCount, productInfo, name })
}

export const dataLayerEventDetailsPageShow = data => {
  const {
    name = "",
    listInfo = {},
    quantityObject = {},
    records = [],
    listItemsPrice = {},
    persona,
  } = data
  const { adobeDataLayer: { getState } = {} } = window
  const page = getState?.("page") || {}
  const productInfo = dataLayerEventDetailsPageProductInfo(
    records,
    quantityObject,
    listItemsPrice,
    persona
  )
  page.category["pageType"] = `${KOHLER}:${MY_ACCOUNT}:${MY_LISTS}:detail`
  page.category["primaryCategory"] = `${KOHLER}:${MY_ACCOUNT}`
  page.category["subCategory1"] = `${MY_ACCOUNT}:${MY_LISTS}`
  page.category["subCategory2"] =
    `${KOHLER}:${MY_ACCOUNT}:${MY_LISTS}:${name}`?.toLowerCase()
  page.category["subCategoryID"] = `${MY_LISTS}:${name}`?.toLowerCase()
  page.category["subCategoryName"] = `${MY_LISTS}:${name}`?.toLowerCase()
  page.pageInfo["pageName"] =
    `${KOHLER}:${MY_ACCOUNT}:${MY_LISTS}:${LIST_DETAIL}:${name}`?.toLowerCase()
  page.pageInfo["previousPage"] = `${KOHLER}:${MY_ACCOUNT}:${ACCOUNT_SETTINGS}`
  page.pageInfo["title"] = `My Lists | ${name} | KOHLER`?.toLowerCase()
  page.listInfo = { ...listInfo }

  if (page.pageName && page.pageUrl) {
    delete page.pageName
    delete page.pageUrl
  }
  setTimeout(() => {
    window.adobeDataLayer.splice(1, 1)
    window.adobeDataLayer.push({
      event: "cmp:show",
      productInfo: productInfo,
      page,
    })
  }, 2500)
}
